import React, { memo, useEffect } from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Link from '@atlaskit/link/link';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message/message';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { type NLPSearchTeamResultDataType } from '../../ai-answer-dialog/types';
import { getTypedJSONResultData } from '../../ai-answer-dialog/utils/get-typed-json-result-data';
import { useSAINUsageCriteriaMet } from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { messages } from '../../messages';
import { AIDisclaimerAndFeedback } from '../../v2/common/ui/ai-disclaimer-and-feedback';
import { getEditDistanceRatio } from '../gql/hydrate-team-card/edit-distance';
import { KnowledgeCardsIntentType } from '../knowledge-cards-intent-type';
import { onKnowledgeCardShown } from '../on-knowledge-card-shown';
import { SeparatorLineRefresh } from '../separator-line';

import { TeamCardBody } from './team-card-body';
import { TeamCardHeader } from './team-card-header';

const CardContainerStyles = cssMap({
	default: {
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	refresh: {
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		gap: token('space.200'),
	},
	disclaimer: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
});

export const TeamKnowledgeCard: React.MemoExoticComponent<
	({ onFMPReached }: { onFMPReached?: () => void }) => React.JSX.Element
> = memo(({ onFMPReached }: { onFMPReached?: () => void }): React.JSX.Element => {
	const {
		commonAttributes,
		userDetails: { id: currentUserId },
	} = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { answerString, query } = commonAttributes;
	const data = getTypedJSONResultData({ answerString }).parsedResult as NLPSearchTeamResultDataType;
	const editDistanceRatio = getEditDistanceRatio(query, data.name);

	useEffect(() => {
		if (data.id) {
			onFMPReached?.();
			fireSAINUsageEvent({ commonAttributes });
			fireAnalyticsEvent(
				onKnowledgeCardShown({
					...commonAttributes,
					cardType: KnowledgeCardsIntentType.TEAM,
					extraAttributes: {
						editDistance: editDistanceRatio,
					},
				}),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const showKnowledgeCardBody =
		(data.recentActivity?.length || 0) > 0 || (data.members?.length || 0) > 0;

	return (
		<Stack space="space.200">
			{fg('enable_team_site_assignment') && (
				<SectionMessage>
					<FormattedMessage {...messages.ai_answer_dialog_teams_not_found_message} />{' '}
					<Link href="https://support.atlassian.com/platform-experiences/docs/understanding-team-visibility/">
						<FormattedMessage {...messages.ai_answer_dialog_teams_not_found_message_link} />
					</Link>
				</SectionMessage>
			)}
			{data.id ? (
				<Stack xcss={CardContainerStyles.refresh} testId="team-knowledge-card">
					<TeamCardHeader
						name={data.name}
						avatarUrl={data.avatarUrl}
						profileUrl={data.url}
						teamId={data.id}
					/>
					{showKnowledgeCardBody && <SeparatorLineRefresh />}
					<Box>
						{showKnowledgeCardBody && (
							<TeamCardBody
								currentUserId={currentUserId || ''}
								recentActivity={data.recentActivity}
								recommendedUsers={data.members}
								viewAllUrl={data.url || ''}
								teamId={data.id}
							/>
						)}
						<Box xcss={CardContainerStyles.disclaimer}>
							<AIDisclaimerAndFeedback showFeedbackButtons />
						</Box>
					</Box>
				</Stack>
			) : null}
		</Stack>
	);
});
